<script setup lang="ts">
import { defineEmits, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import { FavorableMonthFilter } from "@/types/calculators";

const props = defineProps<{
  allDays: number;
  goldenDays: number;
  commerceDays: number;
  marriageDays: number;
  livingMoveDays: number;
  careerDays: number;
  travelDays: number;
  medicalDays: number;
}>();
const emit = defineEmits<{
  (e: "selectFilter", filter: FavorableMonthFilter): void;
}>();

const { t } = useI18n();

const suitsDaysCount = ref(props.allDays);
const selectedFilter = ref<FavorableMonthFilter>("allDays");

function handleSelectFilter(filter: FavorableMonthFilter) {
  selectedFilter.value = filter;
  suitsDaysCount.value = props[filter];
  emit("selectFilter", filter);
}

const getButtonClasses = (type: FavorableMonthFilter) => {
  return selectedFilter.value === type
    ? "bg-active text-white border-active font-semibold shadow-sm"
    : "bg-secondary text-brown border-divider hover:bg-hover";
};
const getSpanClasses = (type: FavorableMonthFilter) => {
  return selectedFilter.value === type
    ? "bg-white/20 text-white"
    : "bg-white text-muted";
};
</script>

<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between">
      <span class="text-[11px] font-bold uppercase tracking-wider text-muted">
        {{ t("calculators.personal-date.month-table.filters.title") }}
      </span>
      <i18n-t
        keypath="calculators.personal-date.month-table.filters.suits-for-days"
        tag="span"
        class="text-xs text-alternative-gray"
      >
        <template #suits>
          <strong class="text-active">{{ suitsDaysCount }}</strong>
        </template>
        <template #total>
          <span>{{ allDays }}</span>
        </template>
      </i18n-t>
    </div>
    <div class="flex items-center gap-2 overflow-x-auto pb-1.5 -mx-1 px-1">
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('allDays')"
        @click="handleSelectFilter('allDays')"
      >
        <span>📅</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.all-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('allDays')"
        >
          {{ allDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('goldenDays')"
        @click="handleSelectFilter('goldenDays')"
      >
        <span>★</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.golden-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('goldenDays')"
        >
          {{ goldenDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('commerceDays')"
        @click="handleSelectFilter('commerceDays')"
      >
        <span>💼</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.commerce-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('commerceDays')"
        >
          {{ commerceDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('marriageDays')"
        @click="handleSelectFilter('marriageDays')"
      >
        <span>💍</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.marriage-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('marriageDays')"
        >
          {{ marriageDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('livingMoveDays')"
        @click="handleSelectFilter('livingMoveDays')"
      >
        <span>🏠</span>
        <span>
          {{
            t("calculators.personal-date.month-table.filters.living-move-days")
          }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('livingMoveDays')"
        >
          {{ livingMoveDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('careerDays')"
        @click="handleSelectFilter('careerDays')"
      >
        <span>📈</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.career-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('careerDays')"
        >
          {{ careerDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('travelDays')"
        @click="handleSelectFilter('travelDays')"
      >
        <span>✈️</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.travel-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('travelDays')"
        >
          {{ travelDays }}
        </span>
      </button>
      <button
        class="px-3 py-2 rounded-xl border text-xs transition-all flex items-center gap-1.5 shrink-0"
        :class="getButtonClasses('medicalDays')"
        @click="handleSelectFilter('medicalDays')"
      >
        <span>🩺</span>
        <span>
          {{ t("calculators.personal-date.month-table.filters.medical-days") }}
        </span>
        <span
          class="px-1.5 py-0.2 rounded-full text-[10px]"
          :class="getSpanClasses('medicalDays')"
        >
          {{ medicalDays }}
        </span>
      </button>
    </div>
  </div>
</template>
